// Self character card (#vitals): portrait + name + sub + HP/STA bars.
// Shares the `.char-card` shell with the focus target card (top-right) — same
// layout language so both read as "character cards". Pure read-only view over
// `game.hp/maxHp` and `game.stamina/maxStamina`; portrait is the SAME 3D
// headshot pipeline as the focus card (focus-portrait.ts).
//
// Bar icons: oil-grimoire plates under assets/ui/icons/ (served at /ui/icons/).
// Art contract: knowledge-base/reference/ui-icon-art-style.md

import { game } from "./state.ts";
import { $ } from "./dom.ts";
import { DEFAULT_APPEARANCE, type Appearance, type Gear } from "./shared.ts";
import { paintPlayerPortrait } from "./focus-portrait.ts";
import { UI_ICON } from "./ui-icons.ts";

const hpFill = $<HTMLElement>("#hpFill");
const hpLabel = $<HTMLElement>("#hpLabel");
const staFill = $<HTMLElement>("#staFill");
const staLabel = $<HTMLElement>("#staLabel");
const portrait = $<HTMLCanvasElement>("#portrait");
const charName = $<HTMLElement>("#charname");
const hpIcon = $<HTMLImageElement>("#hpIcon");
const staIcon = $<HTMLImageElement>("#staIcon");
hpIcon.src = UI_ICON.health;
staIcon.src = UI_ICON.stamina;

/** Last look/gear used for the portrait — gear refreshes re-paint. */
let lastLook: Appearance = { ...DEFAULT_APPEARANCE };
let lastGear: Gear = {};

function paintPortrait(): void {
  paintPlayerPortrait(portrait, lastLook, lastGear);
}

/** Set the player's name + portrait look (called on sign-in). */
export function setCharacter(name: string, look: Appearance = DEFAULT_APPEARANCE) {
  charName.textContent = name || "Adventurer";
  lastLook = look;
  paintPortrait();
}

/** Re-paint the headshot when worn gear changes (helmets, tunics, etc.). */
export function setPortraitGear(gear: Gear): void {
  lastGear = gear ?? {};
  paintPortrait();
}

// Default portrait once content is importable; blank canvas is fine if not.
paintPortrait();

// Avoid touching the DOM when nothing changed (this runs every frame).
let lastHp = NaN, lastSta = NaN;

export function updateVitals() {
  if (game.hp !== lastHp) {
    lastHp = game.hp;
    hpFill.style.width = `${Math.max(0, (game.hp / game.maxHp) * 100)}%`;
    hpLabel.textContent = `HP ${Math.max(0, Math.round(game.hp))}/${game.maxHp}`;
  }
  if (game.stamina !== lastSta) {
    lastSta = game.stamina;
    staFill.style.width = `${Math.max(0, (game.stamina / game.maxStamina) * 100)}%`;
    staLabel.textContent = `STA ${Math.max(0, Math.round(game.stamina))}/${game.maxStamina}`;
  }
}
